import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { wishlistAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { appToast } from '../utils/toast';
import { useLanguage } from './LanguageContext';
import { buildAuthRedirectState } from '../utils/authRedirect';

interface WishlistContextType {
  ids: number[];
  loading: boolean;
  isInWishlist: (productId: number) => boolean;
  toggleWishlist: (productId: number) => Promise<boolean>;
  refreshWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const useWishlist = (): WishlistContextType => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const [ids, setIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const refreshWishlist = useCallback(async (): Promise<void> => {
    // Favourites require an account: there is no guest wishlist.
    if (!user) {
      setIds([]);
      return;
    }
    try {
      setLoading(true);
      const response = await wishlistAPI.getWishlistIds();
      setIds(Array.isArray(response.data) ? response.data : []);
    } catch {
      // Silent — wishlist is non-critical
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshWishlist();
    } else {
      setIds([]);
    }
  }, [user, refreshWishlist]);

  const isInWishlist = useCallback(
    (productId: number) => ids.includes(productId),
    [ids]
  );

  const toggleWishlist = useCallback(
    async (productId: number): Promise<boolean> => {
      // Guests can't have favourites — prompt them to log in first.
      if (!user) {
        appToast.error(t.toasts.wishlistLoginRequired);
        navigate('/register', { state: buildAuthRedirectState(location) });
        throw new Error('AUTH_REQUIRED');
      }

      const currentlyIn = ids.includes(productId);

      // Optimistic update
      setIds((prev) =>
        currentlyIn ? prev.filter((id) => id !== productId) : [...prev, productId]
      );

      try {
        if (currentlyIn) {
          await wishlistAPI.removeFromWishlist(productId);
        } else {
          await wishlistAPI.addToWishlist(productId);
        }
        return !currentlyIn;
      } catch (error) {
        // Revert on failure
        setIds((prev) =>
          currentlyIn ? [...prev, productId] : prev.filter((id) => id !== productId)
        );
        appToast.error(t.toasts.wishlistUpdateFailed);
        throw error;
      }
    },
    [user, ids, t, navigate, location]
  );

  const value: WishlistContextType = {
    ids,
    loading,
    isInWishlist,
    toggleWishlist,
    refreshWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
